<script>
import {
  calculateChecks,
  getChecksText,
  getCheckSubtitle,
} from '@/utils/checks';
import SignalButton from '@/V2/documents/document-view/SignalButton';
import DocumentRightPanelEmptyState from '@/V2/documents/document-view/DocumentRightPanelEmptyState';
import {
  iAccordion,
  iAccordionItem,
  iAccordionItemSizes,
  iBadge,
  iBadgeColours,
  iIcon,
  iTooltip,
  iTooltipAlignmentMode,
} from '@inscribe/signal';
import palette from '@inscribe/signal/dist/palette';

export default {
  components: {
    DocumentRightPanelEmptyState,
    iAccordion,
    iAccordionItem,
    iBadge,
    iIcon,
    iTooltip,
    SignalButton,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      iAccordionItemSizes,
      iBadgeColours,
      iTooltipAlignmentMode,
      palette,
    };
  },
  computed: {
    shouldShowChecks() {
      return (
        this.checks.total > 0 &&
        Object.keys(this.$store.state.config.rulesSchema).length > 0
      );
    },
    checks() {
      return calculateChecks(this.document, true);
    },
    checksList() {
      const checksCopy = [...this.document.checks];
      return checksCopy.sort((c1, c2) => (c1.passed > c2.passed ? 1 : -1));
    },
    checksDescription() {
      return getChecksText(
        this.checks.passed,
        this.checks.passedCount,
        this.checks.total,
      );
    },
  },
  methods: {
    getCheckSubtitle,
    onButtonClicked(check) {
      this.$emit('open-check-details', check);
    },
    openDecisionEngine() {
      this.$router.push({ name: 'decision-engine-checks' });
    },
  },
};
</script>

<template>
  <i-accordion data-testid="checks-list">
    <i-accordion-item
      title="Document Checks"
      class="half-margin"
      :no-background="true"
      :mount-open="true"
      :size="iAccordionItemSizes.LARGE"
    >
      <div
        slot="extra-title-content"
        class="d-flex flex-row align-items-center justify-content-start i-gap-4"
      >
        <!-- info icon -->
        <i-icon
          symbol="small-question-mark"
          :size="12"
          :colour="palette['light-neutral-80']"
          class="checks-list-help-icon"
        ></i-icon>
        <i-tooltip
          :target="`.checks-list-help-icon`"
          title="Document Checks"
          description="A document check examines specific elements such as document type or date to verify compliance with requirements."
          link-text="Learn more"
          link-external-url="https://help.inscribe.ai/en/articles/5677805-checks"
          :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
        />

        <!-- summary badge -->
        <i-badge
          v-if="checks && checks.total > 0 && checks.passed"
          label="All Checks Passed"
          :colour="iBadgeColours.NEUTRAL"
          :outline="true"
        ></i-badge>
        <div
          v-if="checks && checks.total > 0 && !checks.passed"
          class="failed-count d-flex align-items-center justify-content-start"
          data-testid="failed-count"
        >
          <i-badge
            :label="checksDescription.number + ' Failed'"
            :colour="iBadgeColours.NEUTRAL"
            :outline="true"
          ></i-badge>
        </div>
      </div>
      <div class="document-signals-list d-flex flex-column accordion-content">
        <!-- empty state -->
        <document-right-panel-empty-state
          v-if="!checks || checks.total === 0"
          data-testid="checks-list-empty-state"
          symbol="medium-file-search"
          title="No checks evaluated"
          sub-title="No checks targeted this document"
          button-text="Manage Checks"
          class="i-mx-6"
          @on-button-click="openDecisionEngine"
        ></document-right-panel-empty-state>

        <div
          v-else-if="shouldShowChecks"
          class="checks-list d-flex flex-column i-gap-1"
        >
          <signal-button
            v-for="(check, check_index) in checksList"
            :key="check_index"
            :title="check.name"
            :subtitle="getCheckSubtitle(check)"
            :success="check.passed"
            @button-click="onButtonClicked(check)"
          ></signal-button>
        </div>
      </div>
    </i-accordion-item>
  </i-accordion>
</template>
